<template>
  <c-box>
    <c-box
      v-if="submitting"
      z-index="900000"
      position="fixed"
      bg="black"
      left="0"
      top="0"
      width="100%"
      height="100%"
      opacity="0.5"
      cursor="wait"
    />

    <slot name="tabs" />

    <c-box :py="{ base: 'sm', lg: '3xl' }">
      <slot name="content" />
      <c-flex v-if="!isFinalPage()" mt="3xl">
        <c-flex :flex-direction="{ base: 'column', sm: 'row' }">
          <c-button
            v-if="hasPrevPage()"
            @click="prevPage"
            :disabled="!hasUsablePrevPage() || preventNavigation()"
            mr="xl"
          >
            {{ backButtonTitle }}
          </c-button>

          <c-button
            @click="reset"
            left-icon="trashcan"
            border-color="transparent"
            :pl="{ sm: 'xs' }"
            pr="xs"
            variant="link"
            color="black"
            :mt="{ base: hasPrevPage() ? 'lg' : 'xs', sm: '0' }"
            data-cy="reset-button"
          >
            <c-text as="span" pl="0.4rem">{{ resetButtonTitle }}</c-text>
          </c-button>
        </c-flex>

        <c-flex ml="auto">
          <c-button
            v-if="hasNextPage() && !isSubmitPage()"
            variation="solid"
            @click="nextPage"
            background="brand.beige"
            border-color="brand.beige"
            :disabled="!hasUsableNextPage() || preventNavigation() || validationInProgress"
            data-cy="wizard-next-button"
          >
            {{ nextButtonTitle }}
          </c-button>
          <c-button
            v-else-if="hasNextPage() && isSubmitPage()"
            variation="solid"
            @click="submit"
            background="brand.beige"
            border-color="brand.beige"
            :disabled="!hasUsableNextPage()"
            data-cy="wizard-submit-button"
          >
            {{ submitButtonTitle }}
          </c-button>
        </c-flex>
      </c-flex>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import Vue, { defineComponent } from "vue";
import MobileTabMenu from "@/components/MobileTabMenu.vue";
import { CBox, CButton, CFlex, CText } from "@chakra-ui/vue";
import WizardPage from "./WizardPage.vue";

export default defineComponent({
  name: "WizardForm",
  components: {
    CButton,
    CBox,
    CFlex,
    CText,
    MobileTabMenu,
  },
  props: {
    title: String,
    resetButtonTitle: String,
    backButtonTitle: String,
    nextButtonTitle: String,
    submitButtonTitle: String,
    validated: Boolean,
    submitCallback: Function,
    currentStep: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      submitting: false,
      validationInProgress: false,
      pages: [] as (typeof WizardPage)[],
    };
  },
  methods: {
    hasErrors(): boolean {
      return !!this.pages.find((page) => page.errors);
    },
    reset(): void {
      this.$emit("onReset");
    },
    resetPagesVisitedInfo(): void {
      for (let i = this.currentStep; i < this.pages.length; i++) {
        this.pages[i].isActivated = false;
        this.pages[i].isVisited = false;
      }
    },
    submit(): void {
      const firstErrorPage = this.hasErrors();
      if (!firstErrorPage) {
        const curPage = this.getCurrentPage();
        let newValidationErrors = false;
        if (this.validated) {
          this.touch(curPage);
          newValidationErrors = this.findVuelidationError(this.currentPageChildWithForm(curPage));
        }
        if (!this.curPageHasErrors() && !newValidationErrors) {
          if (this.submitCallback) {
            this.submitting = true;
            this.submitCallback()
              .then(() => {
                for (let i = 0; i <= this.currentStep - 1; i++) {
                  this.pages[i].isDisabled = true;
                  this.pages[i].isVisited = false;
                }
                this.nextPage();
                localStorage.setItem("returnsForm", "");
                this.submitting = false;
              })
              .catch((err) => {
                this.submitting = false;
                this.$emit("onSubmitError", err);
              });
          } else {
            throw new Error("No submit callback specified.");
          }
        }
      } else {
        this.focusPage(firstErrorPage);
      }
    },
    currentPageTitle(): string {
      const page = this.getCurrentPage();
      return page?.title || "";
    },
    currentPageName(): string | undefined {
      const page = this.getCurrentPage();
      return page?.name ?? undefined;
    },
    isSubmitPage(): boolean {
      const page = this.getCurrentPage();
      return !!page?.submitPage;
    },
    isFinalPage(): boolean {
      const page = this.getCurrentPage();
      return !!page?.finalPage;
    },
    curPageHasErrors() {
      const page = this.getCurrentPage();
      return !!page?.errors;
    },
    preventNavigation(): boolean {
      return !!this.getCurrentPage()?.preventNavigation;
    },
    getCurrentPage(): typeof WizardPage {
      return this.pages[this.currentStep - 1];
    },
    addPage(ref: typeof WizardPage): void {
      this.pages.push(ref);
    },
    removePage(ref: typeof WizardPage): void {
      const index = this.pages.indexOf(ref);
      if (index >= 0) {
        this.pages.splice(index, 1);
      }
    },
    isFirstPage(): boolean {
      return this.currentStep === 1;
    },
    isLastPage(): boolean {
      return this.currentStep === this.pages.length;
    },
    hasUsableNextPage(): boolean {
      return this.currentStep < this.pages.length && this.findNextUsablePage();
    },
    hasUsablePrevPage(): boolean {
      return this.currentStep > 1 && this.findPrevUsablePage();
    },
    hasNextPage(): boolean {
      return this.currentStep < this.pages.length;
    },
    hasPrevPage(): boolean {
      return this.currentStep > 1;
    },
    scrollToPageTop(): void {
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    prevPage(): void {
      const page = this.findPrevUsablePage();
      if (page) {
        this.focusPage(page);
        this.scrollToPageTop();
      }
    },
    touch(el: any): void {
      if (el.$v) {
        el.$v.$touch();
      }
      if (el.$children) {
        for (let i = 0; i < el.$children.length; i++) {
          this.touch(el.$children[i]);
        }
      }
    },
    findVuelidationError(currentNode: any): boolean {
      let result;
      if (currentNode.$v && currentNode.$v.$error) {
        result = true;
      } else {
        for (let i = 0; i < currentNode.$children.length; i++) {
          result = this.findVuelidationError(currentNode.$children[i]);
          if (result) {
            return result;
          }
        }
        return false;
      }
      return result;
    },
    focusFirstErrorInput(currentNode: any): boolean {
      if (
        (currentNode.tagName === "INPUT" || currentNode.tagName === "SELECT") &&
        !currentNode.disabled &&
        currentNode.className.indexOf("is-invalid") >= 0
      ) {
        const theField = document.getElementById(currentNode.id)!;
        setTimeout(() => theField.focus(), 10);
        return true;
      }
      if (currentNode.children) {
        for (let i = 0; i < currentNode.children.length; i++) {
          if (this.focusFirstErrorInput(currentNode.children[i])) {
            return true;
          }
        }
      }
      return false;
    },
    focusFirstNotDisabledInput(currentNode: any): boolean {
      if (currentNode.tagName === "INPUT" && !currentNode.disabled) {
        const theField = document.getElementById(currentNode.id);
        setTimeout(() => theField?.focus(), 1);
        return true;
      }
      if (currentNode.children) {
        for (let i = 0; i < currentNode.children.length; i++) {
          if (this.focusFirstNotDisabledInput(currentNode.children[i])) {
            return true;
          }
        }
      }
      return false;
    },
    nextPage(): void {
      const curPage = this.getCurrentPage();
      let newValidationErrors = false;

      if (this.validated) {
        this.touch(curPage);
        newValidationErrors = this.findVuelidationError(this.currentPageChildWithForm(curPage));
      }

      if (!this.curPageHasErrors() && !newValidationErrors) {
        if (curPage.validationPromise) {
          const curForm = this.currentPageChildWithForm(curPage).form;
          this.handleValidation(curPage, curForm)
            .then(() => {
              this.switchToNextPage();
            })
            .catch(() => {});
        } else {
          this.switchToNextPage();
        }
      } else {
        this.focusFirstErrorInput(curPage.$el);
      }
    },
    async handleValidation(curPage: any, curForm: any): Promise<void> {
      this.validationInProgress = true;
      return curPage
        .validationPromise(...curPage.validationParams)
        .then((cities) => {
          if (!cities.includes(curForm.partnerCity)) {
            throw new Error("Invalid partnerCity");
          }
          Vue.set(curForm, "$externalValidationSuccess", undefined);
          this.validationInProgress = false;
        })
        .catch((error) => {
          this.validationInProgress = false;
          Vue.set(curForm, "$externalValidationSuccess", false);
          throw error;
        });
    },
    switchToNextPage(): void {
      const page = this.findNextUsablePage();
      if (page) {
        this.focusPage(page);
        this.scrollToPageTop();
      }
    },
    currentPageChildWithForm(curPage: typeof WizardPage): any {
      return curPage.$children[0].$children[1];
    },
    focusPage(page: any): void {
      const curPage = this.getCurrentPage();
      if (page.step > curPage.step) {
        this.touch(curPage);
        if (this.findVuelidationError(this.currentPageChildWithForm(curPage))) {
          curPage.errors = true;
        }
      }
      this.switchToPage(page);
    },
    switchToPage(page: any): void {
      const focusPage = page;
      this.setCurrentStep(focusPage.step);
      focusPage.activate();
      focusPage.show();
      if (!this.focusFirstErrorInput(focusPage.$el)) {
        this.focusFirstNotDisabledInput(focusPage.$el);
      }
    },
    setCurrentStep(step: number): void {
      this.$router.push({
        query: {
          step: step.toString(),
        },
      });
    },
    onStepChange(): void {
      const isValidStep = this.pages[this.currentStep - 1]?.isDisabled === false;
      if (isValidStep) {
        return;
      }
      const page = this.findPrevUsablePage();
      if (page) {
        this.$router.replace({
          query: {
            step: page.step.toString(),
          },
        });
      }
    },
    findFirstUsablePage(): any | undefined {
      return this.pages.find((page) => !page.disabled);
    },
    findNextUsablePage(): any | undefined {
      return this.pages.find((page) => page.step > this.currentStep && !page.disabled);
    },
    findPrevUsablePage(): any | undefined {
      if (this.currentStep > 1) {
        const currentStepIndex = this.currentStep - 1;
        for (let i = currentStepIndex - 1; i >= 0; i--) {
          const page = this.pages[i];
          if (!page.isDisabled) {
            return page;
          }
        }
      }
      return undefined;
    },
  },
  watch: {
    "$route.query.step": "onStepChange",
  },
  mounted() {
    this.$nextTick(() => {
      const page = this.findFirstUsablePage() || this.pages[0];
      if (page) {
        this.focusPage(page);
      } else {
        throw new Error("No pages defined for FormWizard component.");
      }
      const hasSubmitPage = this.pages.filter((c) => c.submitPage);
      if (!hasSubmitPage) {
        this.pages[this.pages.length - 1].submitPage = true;
      }
    });
  },
});
</script>
